import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getUserModules } from '../../utilities/api';

const enrichModules = (modules, clients, branches, users) =>
    modules.map((m) => ({
        ...m,
        client_name: clients.find((c) => String(c.client_id) === String(m.client_id))?.client_name || m.client_name || '',
        branch_name: branches.find((b) => String(b.branch_id) === String(m.branch_id))?.branch_name || m.branch_name || '',
        // backend JOIN returns wrong username — always prefer users array lookup
        username: users.find((u) => String(u.userId) === String(m.user_id))?.username || ''
    }));

export const fetchUserModules = createAsyncThunk('userModules/fetch', async (_, { rejectWithValue }) => {
    try {
        return await getUserModules();
    } catch (error) {
        return rejectWithValue(error.message || 'Failed to fetch user modules');
    }
});

const initialState = {
    enrichedModules: [],
    allModules: [],
    clients: [],
    allBranches: [],
    allUsers: [],
    selectedClient: null,
    selectedBranch: null,
    selectedUser: null,
    loading: false,
    error: null
};

const usermodulesSlice = createSlice({
    name: 'userModules',
    initialState,
    reducers: {
        setSelectedClient: (state, action) => {
            state.selectedClient = action.payload;
            state.selectedBranch = null;
            state.selectedUser = null;
        },
        setSelectedBranch: (state, action) => {
            state.selectedBranch = action.payload;
            state.selectedUser = null;
        },
        setSelectedUser: (state, action) => {
            state.selectedUser = action.payload;
        },
        resetFilters: (state) => {
            state.selectedClient = null;
            state.selectedBranch = null;
            state.selectedUser = null;
        },
        updateModuleStatus: (state, action) => {
            const { module_user_id, status } = action.payload;
            const update = (arr) => arr.map((m) => String(m.module_user_id) === String(module_user_id) ? { ...m, status } : m);
            state.allModules      = update(state.allModules);
            state.enrichedModules = update(state.enrichedModules);
        },
        bulkUpdateStatus: (state, action) => {
            const { module_user_ids, status } = action.payload;
            const ids = module_user_ids.map(String);
            const update = (arr) => arr.map((m) => ids.includes(String(m.module_user_id)) ? { ...m, status } : m);
            state.allModules      = update(state.allModules);
            state.enrichedModules = update(state.enrichedModules);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUserModules.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUserModules.fulfilled, (state, action) => {
                state.loading = false;
                const { clients = [], branchs = [], users = [], modules = [] } = action.payload || {};
                state.clients      = clients;
                state.allBranches  = branchs;
                state.allUsers     = users;
                state.allModules   = modules;
                state.enrichedModules = enrichModules(modules, clients, branchs, users);
            })
            .addCase(fetchUserModules.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    }
});

export const { setSelectedClient, setSelectedBranch, setSelectedUser, resetFilters, updateModuleStatus, bulkUpdateStatus } = usermodulesSlice.actions;

export const selectEnrichedModules = (state) => state.userModules.enrichedModules;
export const selectAllModules      = (state) => state.userModules.allModules;
export const selectClients         = (state) => state.userModules.clients;
export const selectAllBranches     = (state) => state.userModules.allBranches;
export const selectAllUsers        = (state) => state.userModules.allUsers;
export const selectSelectedClient  = (state) => state.userModules.selectedClient;
export const selectSelectedBranch  = (state) => state.userModules.selectedBranch;
export const selectSelectedUser    = (state) => state.userModules.selectedUser;
export const selectLoading         = (state) => state.userModules.loading;
export const selectError           = (state) => state.userModules.error;

export default usermodulesSlice.reducer;
